import { useEffect, useState } from "react";
import { FaChartLine, FaArrowUp, FaArrowDown, FaMinus } from "react-icons/fa";
import { useGetFirmConsumptionSummaryQuery } from "../../../../store";
import { FirmDropdown } from "../../../atoms";
import useFirmScopeSelection, { toScopeFirmOption } from "../../../../Hooks/useFirmScopeSelection";

const formatNumber = (n) => new Intl.NumberFormat("tr-TR").format(n ?? 0);
const formatSigned = (n) => (n > 0 ? `+${formatNumber(n)}` : formatNumber(n));

const DeltaInline = ({ deltaPct, deltaAbs }) => {
  if (deltaPct === null) {
    return <span className="text-[11px] font-bold text-sixth">Yeni</span>;
  }
  if (deltaPct === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-gray-500">
        <FaMinus className="w-2 h-2" /> %0
      </span>
    );
  }
  if (deltaPct > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-sixth">
        <FaArrowUp className="w-2 h-2" /> %{deltaPct}
        <span className="font-semibold text-[10px] text-gray-500 ml-0.5">
          ({formatSigned(deltaAbs)})
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-fifth">
      <FaArrowDown className="w-2 h-2" /> %{Math.abs(deltaPct)}
      <span className="font-semibold text-[10px] text-gray-500 ml-0.5">
        ({formatSigned(deltaAbs)})
      </span>
    </span>
  );
};

const PeriodCell = ({ period, deviceCount }) => {
  const isZero = (period?.current ?? 0) === 0;
  const showDelta = !isZero || (period?.previous ?? 0) !== 0;
  return (
    <div className="flex flex-col items-center justify-center py-2">
      <span className="text-base sm:text-lg font-bold text-main leading-none">
        {formatNumber(period?.current)}
      </span>
      {showDelta ? (
        <DeltaInline deltaPct={period?.deltaPct ?? 0} deltaAbs={period?.deltaAbs ?? 0} />
      ) : (
        <span className="text-[11px] font-bold text-gray-300">—</span>
      )}
      {deviceCount > 0 && (
        <span className="text-[10px] text-gray-400 mt-0.5">
          {formatNumber(period?.perDevice ?? 0)}/cihaz
        </span>
      )}
    </div>
  );
};

// useSummaryQuery: rol grubuna göre doğru endpoint'e giden RTK Query hook'u
// (storage varsayılan; dealer InfoTab dealer hook'unu geçirir).
const FirmConsumptionSummaryCard = ({
  firmId,
  token,
  useSummaryQuery = useGetFirmConsumptionSummaryQuery,
}) => {
  // Alt firma kapsamı — VARSAYILAN: firma + TÜM alt firmaları
  // (kullanıcı kararı 2026-09-14). Ayrıntı: hooks/useFirmScopeSelection.
  const [firmOptions, setFirmOptions] = useState([]);
  const { requestFirmIds, selectedFirms, onChange, hasSubFirms } =
    useFirmScopeSelection(firmOptions);

  const { data, isLoading, isError, isFetching } = useSummaryQuery(
    {
      firmId,
      token,
      firmIds: requestFirmIds,
    },
    { skip: !firmId || !token }
  );

  const products = data?.data?.products ?? [];
  const isEmpty = products.length === 0;

  // Seçici seçenekleri — sunucudan gelen kök + alt firma listesi. Liste
  // state'e taşınır: kapsam daraltıldığında sunucu yine TÜM izinli firmaları
  // döndürür, ama yanıt arasında seçici boşalmasın diye son bilinen liste
  // korunur.
  const serverFirms = data?.data?.firms;
  useEffect(() => {
    if (!Array.isArray(serverFirms) || serverFirms.length === 0) return;
    setFirmOptions(
      serverFirms.map(toScopeFirmOption),
    );
  }, [serverFirms]);

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/30 shrink-0">
          <FaChartLine className="w-4 h-4 text-main" />
        </div>
        <h3 className="text-xs sm:text-sm font-Bold text-gray-700 uppercase tracking-wide">
          Kota Tüketimi
        </h3>
        {isFetching && !isLoading && (
          <div className="w-3.5 h-3.5 shrink-0 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        )}
        {/* Alt firma seçici — sağ üstte, yalnızca alt firma varsa.
            Varsayılan TÜMÜ: kart alt firmaların tüketimini de kapsar. */}
        {hasSubFirms && (
          <div className="ml-auto w-40 sm:w-52 shrink-0">
            <FirmDropdown
              compact
              multiple
              firms={firmOptions}
              value={selectedFirms}
              placeholder="Firma seçin"
              onChange={onChange}
            />
          </div>
        )}
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-12 w-full bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      )}

      {!isLoading && isError && (
        <p className="text-sm text-fifth italic">Tüketim bilgisi yüklenemedi</p>
      )}

      {!isLoading && !isError && isEmpty && (
        <p className="text-sm text-gray-400 italic">Bu firma için tüketim verisi bulunmuyor</p>
      )}

      {!isLoading && !isError && !isEmpty && (
        <div className="overflow-x-auto overflow-y-auto max-h-64 -mx-1">
          <table className="w-full">
            <thead className="sticky top-0 bg-white">
              <tr>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-2 py-1">
                  Ürün
                </th>
                <th className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-2 py-1">
                  Cihaz
                </th>
                <th className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-2 py-1">
                  Günlük
                </th>
                <th className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-2 py-1">
                  Haftalık
                </th>
                <th className="text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-2 py-1">
                  Aylık
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.map((p) => (
                <tr key={p.productName}>
                  <td className="text-sm font-semibold text-gray-800 px-2 py-1.5 align-middle">
                    {p.productName}
                  </td>
                  <td className="text-center px-2 py-1.5 align-middle">
                    <span className="inline-flex items-baseline gap-0.5">
                      <span className="text-sm font-bold text-main">{formatNumber(p.deviceCount ?? 0)}</span>
                      <span className="text-[10px] text-gray-400">cihaz</span>
                    </span>
                  </td>
                  <td className="px-1">
                    <PeriodCell period={p.daily} deviceCount={p.deviceCount} />
                  </td>
                  <td className="px-1">
                    <PeriodCell period={p.weekly} deviceCount={p.deviceCount} />
                  </td>
                  <td className="px-1">
                    <PeriodCell period={p.monthly} deviceCount={p.deviceCount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FirmConsumptionSummaryCard;
